<script setup lang="ts">
// Variant of EmojiListGrid. Differences:
// - Renders emojis in a single horizontal row.
// - No grouping by category.
// - No tooltip on hover.
// - No checkmark for selected emoji.
import { getEmojiKey } from '@@/bits/emoji'
import OzVirtualList from '@@/library/v4/components/OzVirtualList.vue'
import EmojiListItem from '@@/vuecomponents/EmojiListItem.vue'
import type { Emoji } from '@padlet/arvo'
import { computed, readonly, ref, shallowRef } from 'vue'

const props = withDefaults(
  defineProps<{
    isLoading: boolean
    emojis: Emoji[]
    darkMode: boolean
    iconSize?: number
    hoveredIconSize?: number
    draggable?: boolean
  }>(),
  {
    iconSize: 40,
    hoveredIconSize: undefined,
    draggable: false,
  },
)

const emit = defineEmits<{
  (e: 'pick', url: string): void
  (e: 'category-visible', categoryLabel: string): void
}>()

const ITEM_GAP = 8 // px

const estimateItemSize = (): number => {
  const EMOJI_PADDING = 4 // padding around each emoji (same as EmojiListGrid)
  return props.iconSize + EMOJI_PADDING * 2
}

// Since it's a single row -> row height is the same as item size
const rowHeight = computed(() => estimateItemSize())

const virtualList = shallowRef<InstanceType<typeof OzVirtualList>>()

// Track the currently visible emoji index and category
const visibleEmojiIndex = ref<number>(0)
const visibleCategoryLabel = ref<string | null>(null)

const isScrolled = computed<boolean>(() => {
  return (virtualList.value?.scrollOffset ?? 0) > 0
})

/**
 * Calculates which emoji is currently visible based on horizontal scroll position
 */
const detectVisibleEmoji = (): void => {
  const scrollOffset = virtualList.value?.scrollOffset ?? 0
  const itemWidth = estimateItemSize()

  // Calculate which emoji index is at the start of the viewport
  const newEmojiIndex = Math.floor(scrollOffset / (itemWidth + ITEM_GAP))
  const clampedIndex = Math.max(0, Math.min(newEmojiIndex, props.emojis.length - 1))

  if (clampedIndex !== visibleEmojiIndex.value) {
    visibleEmojiIndex.value = clampedIndex

    // Update visible category based on the emoji at this index
    const visibleEmoji = props.emojis[clampedIndex]
    if (visibleEmoji?.category) {
      const categoryLabel = visibleEmoji.category.label
      if (categoryLabel !== visibleCategoryLabel.value) {
        visibleCategoryLabel.value = categoryLabel
        emit('category-visible', categoryLabel)
      }
    }
  }
}

const handleScroll = (): void => {
  detectVisibleEmoji()
}

/**
 * Scrolls to the specified emoji by its flat array index
 */
const scrollToEmoji = (emojiIndex: number) => {
  if (emojiIndex < 0 || emojiIndex >= props.emojis.length) {
    return
  }

  virtualList.value?.scrollToIndex(emojiIndex, {
    align: 'start',
    behavior: 'auto',
  })
}

defineExpose({
  visibleEmojiIndex: readonly(visibleEmojiIndex),
  isScrolled,
  scrollToEmoji,
})
</script>

<template>
  <!-- Loading state -->
  <div v-if="isLoading" class="relative mb-4">
    <div
      class="flex items-center gap-2"
      :style="{
        height: `${rowHeight}px`,
      }"
    >
      <div
        v-for="index in 20"
        :key="index"
        :class="[
          'rounded-xl',
          {
            'bg-color-change-light motion-reduce:bg-button-hover-light': !darkMode,
            'bg-color-change-dark motion-reduce:bg-button-hover-dark': darkMode,
          },
        ]"
        :style="{
          width: `${iconSize}px`,
          height: `${iconSize}px`,
        }"
      />
    </div>
  </div>

  <OzVirtualList
    v-else
    ref="virtualList"
    :direction="'horizontal'"
    :items="emojis"
    :estimate-item-size="estimateItemSize"
    :item-gap="ITEM_GAP"
    :class="[
      'overflow-y-hidden',
      // It's a design choice to not show a scrollbar in single-row layout
      'no-scrollbar',
      'mb-4',
    ]"
    :style="{
      height: `${rowHeight}px`,
    }"
    @scroll="handleScroll"
  >
    <template #item="{ data: emoji }">
      <EmojiListItem
        :data-key="getEmojiKey(emoji)"
        :emoji="emoji"
        :dark-mode="darkMode"
        :icon-size="iconSize"
        :hovered-icon-size="hoveredIconSize"
        :draggable="draggable"
        class="w-min"
        @pick="$emit('pick', emoji.url)"
      />
    </template>
  </OzVirtualList>
</template>

<style scoped>
@keyframes colorChangeLight {
  0%,
  100% {
    @apply bg-button-hover-light;
  }
  50% {
    @apply bg-button-emoji-other-light;
  }
}

@keyframes colorChangeDark {
  0%,
  100% {
    @apply bg-button-hover-dark;
  }
  50% {
    @apply bg-button-emoji-hover-dark;
  }
}

.bg-color-change-light {
  @apply transition-colors duration-500;
  animation: colorChangeLight 4s infinite;
}

.bg-color-change-dark {
  @apply transition-colors duration-500;
  animation: colorChangeDark 4s infinite;
}
</style>
